'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  logoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Composición "marca": el LOGO protagoniza en lugar del nombre escrito. Para el
 * negocio que ya tiene una marca reconocible — el cliente la busca por el logo,
 * no por la tipografía.
 *
 * Solo lee tokens (--ms-*), así que cualquier tema puede elegirla sin heredar
 * el color de firma de otro. Sin logo cae al nombre en la display del tema: la
 * página nunca queda sin encabezado (el panel además esconde esta opción hasta
 * que el merchant sube uno).
 */
export function HeroMarca({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  logoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {heroVideoUrl ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          src={heroVideoUrl}
        />
      ) : heroImageUrl ? (
        <Image
          src={heroImageUrl}
          alt={businessName}
          fill
          priority
          sizes="100vw"
          className="object-cover"
        />
      ) : null}
      {hasMedia && (
        <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />
      )}

      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(660px, 86vh)' }}
      >
        {city && (
          <span
            className="mb-7 text-[11px] font-semibold uppercase tracking-[0.34em] pl-[0.34em]"
            style={{ color: 'var(--ms-accent)' }}
          >
            {city}
          </span>
        )}

        {logoUrl ? (
          // Alto fijo por breakpoint y `w-auto`: el logo de un merchant puede ser
          // cuadrado, apaisado o vertical, y escalar por ancho reventaría los dos
          // últimos. `object-contain` nunca lo recorta.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={logoUrl}
            alt={businessName}
            className="h-24 w-auto max-w-[76vw] object-contain sm:h-32 lg:h-40"
            style={{ filter: hasMedia ? 'drop-shadow(0 8px 28px rgba(0,0,0,0.45))' : undefined }}
          />
        ) : (
          <h1
            className="leading-[1.04] text-[clamp(40px,8vw,84px)]"
            style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
          >
            {businessName}
          </h1>
        )}

        {tagline && (
          <p
            className="mt-6 max-w-xl text-[16px] sm:text-[18px] leading-relaxed"
            style={{ color: 'var(--ms-text-soft)' }}
          >
            {tagline}
          </p>
        )}

        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#5fcf80'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-4 text-[13px] font-bold uppercase tracking-[0.14em] transition hover:brightness-110 active:scale-[0.98]"
            style={{
              background: 'var(--ms-accent)',
              color: 'var(--ms-bg)',
              borderRadius: 'var(--ms-radius)',
              boxShadow: 'var(--ms-card-shadow)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-semibold uppercase tracking-[0.14em] transition"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
